import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Table } from '@quintoandar-tokko/jopijs';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import parentStyles from '../dashboardTables.module.scss';
import specificStyles from './inquiriesReceived.module.scss';
import LoadingMoreSkeleton from '../components/LoadingMoreSkeleton';
import { getPerformanceInquiriesReceivedTable } from '@/store/slices/components/dashboard/service';
import buildQuery from '../../utils/buildQuery';
import useObserver from '@/hooks/useObserver';
import { setPerformanceModals } from '@/store/slices/components/dashboard/DashboardSlice';
import HeaderCell from '../components/HeaderCell';
import buildTableQuery from '../../utils/buildTableQuery';
import { TABLE_SORT_FIELDS } from '../../constants';
import InquiriesReceivedTableRow from './InquiriesReceivedTableRow';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function InquiriesReceivedTable() {
  const dispatch = useDispatch();
  const isAdmin = useSelector(
    (state) => state.AuthSlice.permissions.is_company_admin
  );
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { inquiriesReceived } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const { ref: loadMoreRef, isInViewport } = useObserver();
  const track = useTrackPerformanceTable();

  const headers = [
    {
      id: 1,
      title: Trans(translations, 'Property'),
      subtitle: Trans(translations, 'Location'),
      field: TABLE_SORT_FIELDS.PROPERTY_ADDRESS,
    },
    {
      id: 2,
      title: Trans(translations, 'Operation'),
      subtitle: Trans(translations, 'PropertyType'),
      field: TABLE_SORT_FIELDS.OPERATION,
    },
    {
      id: 3,
      title: Trans(translations, 'Contact'),
      field: TABLE_SORT_FIELDS.CONTACT_NAME,
    },
    {
      id: 4,
      title: Trans(translations, 'Origin'),
      field: TABLE_SORT_FIELDS.ORIGIN,
    },
    isAdmin && {
      id: 5,
      title: Trans(translations, 'Agent'),
      field: TABLE_SORT_FIELDS.AGENT_NAME,
    },
    {
      id: 6,
      title: Trans(translations, 'IncomingDate'),
      subtitle: Trans(translations, 'Hour'),
      field: TABLE_SORT_FIELDS.ENTRY_DATE,
    },
  ].filter(Boolean);

  useEffect(() => {
    if (!inquiriesReceived.has_next) return;
    if (!isInViewport || inquiriesReceived.loading) return;

    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(inquiriesReceived, {
      withNextPage: true,
    });

    dispatch(getPerformanceInquiriesReceivedTable({ query, tableQuery }));
  }, [isInViewport]);

  const handleCellClick = (header) => {
    dispatch(
      setPerformanceModals({
        key: 'inquiriesReceived',
        subkey: 'sortBy',
        value: header.field,
      })
    );
    track.inquiriesReceived(TRACK_ELEMENT.COLUMN_ORDER);
  };

  return (
    <Table
      id="inquiries-received-table"
      className={`${parentStyles.table} ${
        isAdmin
          ? specificStyles.inquiriesReceivedTableLarge
          : specificStyles.inquiriesReceivedTableShort
      }`}
    >
      <Table.Header className={parentStyles.tableHeader}>
        {headers.map((header) => (
          <HeaderCell
            key={header.id}
            id={header.id}
            title={header.title}
            subtitle={header.subtitle}
            field={header.field}
            sortBy={inquiriesReceived.sortBy}
            sortDirection={inquiriesReceived.sortDirection}
            onClick={() => handleCellClick(header)}
          />
        ))}
      </Table.Header>

      <Table.Rows>
        {inquiriesReceived.list.map((inquiry) => (
          <InquiriesReceivedTableRow key={inquiry.id} inquiry={inquiry} />
        ))}

        <Box as="tr" ref={loadMoreRef} style={{ height: 1, opacity: 0 }} />

        {(inquiriesReceived.loading || inquiriesReceived.has_next) && (
          <LoadingMoreSkeleton columns={headers.length} />
        )}
      </Table.Rows>
    </Table>
  );
}
